import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:3001/api'
  : 'https://api.empathos.com.au/api';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [show2FAField, setShow2FAField] = useState(false);
  const [checking2FA, setChecking2FA] = useState(false);
  
  // Superuser username (should match backend SUPERUSER_USERNAME)
  // Update this to match your backend SUPERUSER_USERNAME env variable
  const SUPERUSER_USERNAME = process.env.REACT_APP_SUPERUSER_USERNAME || 'superuser1';

  // Check for invite or reset token in URL
  useEffect(() => {
    const inviteToken = searchParams.get('inviteToken');
    const resetTokenParam = searchParams.get('resetToken');
    
    if (inviteToken) {
      // Handle invite acceptance
      handleInviteAccept(inviteToken);
    } else if (resetTokenParam) {
      setResetToken(resetTokenParam);
      setShowPasswordReset(false); // Don't show the "forgot password" form, show the reset form
      // Scroll to reset password form after a brief delay
      setTimeout(() => {
        const resetForm = document.getElementById('reset-password-form');
        if (resetForm) {
          resetForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      // Clear reset token if not in URL
      setResetToken('');
      setShowPasswordReset(false);
      setResetSuccess(false);
      setError('');
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [searchParams]);
  
  // Check if email matches superuser pattern
  const isSuperuserEmail = () => {
    if (!formData.email) return false;
    // Check if email is superuser@empathos.com.au or just the username part
    const emailLower = formData.email.toLowerCase().trim();
    const superuserLower = SUPERUSER_USERNAME.toLowerCase();
    return emailLower === `${superuserLower}@empathos.com.au` || 
           emailLower === superuserLower;
  };

  const handleChange = async (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    
    // Check 2FA status when email changes
    if (e.target.name === 'email') {
      setTwoFactorCode('');
      setShow2FAField(false);
      
      const email = e.target.value.trim();
      
      // Check if it's a superuser email (always show 2FA)
      if (isSuperuserEmail()) {
        setShow2FAField(true);
        return;
      }
      
      // For other emails, check with backend if they have 2FA enabled
      if (email && email.includes('@')) {
        setChecking2FA(true);
        try {
          const response = await fetch(`${API_URL}/auth/check-2fa`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          if (response.ok) {
            const data = await response.json();
            setShow2FAField(data.twoFactorEnabled || false);
          }
        } catch (err) {
          // Silently fail - don't show error for 2FA check
          console.error('Error checking 2FA status:', err);
        } finally {
          setChecking2FA(false);
        }
      }
    }
  };
  
  const handle2FAChange = (e) => {
    setTwoFactorCode(e.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check if this is a superuser login attempt
      const isSuperuser = isSuperuserEmail();
      
      if (isSuperuser) {
        // Superuser login requires 2FA
        if (!twoFactorCode) {
          setError('2FA code is required for superuser login');
          setLoading(false);
          return;
        }
        
        // Extract username from email (remove @empathos.com.au if present)
        const username = formData.email.includes('@') 
          ? formData.email.split('@')[0] 
          : formData.email;
        
        const response = await fetch(`${API_URL}/auth/superuser-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username,
            password: formData.password,
            twoFactorCode: twoFactorCode,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Superuser login failed');
        }

        // Store token
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Redirect to platform or 2FA setup if required
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        let platformUrl;
        
        if (data.twoFactorSetupRequired) {
          // Redirect to 2FA setup
          platformUrl = isLocalhost
            ? `http://localhost:3002/2fa/setup?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`
            : `https://platform.empathos.com.au/2fa/setup?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`;
        } else {
          // Normal redirect
          platformUrl = isLocalhost
            ? `http://localhost:3002?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`
            : `https://platform.empathos.com.au?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`;
        }
        window.location.href = platformUrl;
      } else {
        // Regular user login (including admins)
        const loginBody = {
          email: formData.email,
          password: formData.password,
        };
        
        // Include 2FA code if field is shown
        if (show2FAField && twoFactorCode) {
          loginBody.twoFactorCode = twoFactorCode;
        }
        
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(loginBody),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Login failed');
        }

        // Store token
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Redirect to platform subdomain
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        let platformUrl;
        if (isLocalhost) {
          if (data.user.passwordChangeRequired) {
            platformUrl = `http://localhost:3002/change-password?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`;
          } else {
            platformUrl = `http://localhost:3002?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`;
          }
        } else {
          // Production: Pass token via URL since localStorage is domain-specific
          if (data.user.passwordChangeRequired) {
            platformUrl = `https://platform.empathos.com.au/change-password?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`;
          } else {
            platformUrl = `https://platform.empathos.com.au?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}`;
          }
        }
        window.location.href = platformUrl;
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset email');
      }

      setResetSuccess(true);
      setResetEmail('');
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken,
          newPassword: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Password reset failed');
      }

      setResetSuccess(true);
      setError('');
      // Clear reset token and redirect to login after 2 seconds
      setTimeout(() => {
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');
        setResetSuccess(false);
        setShowPasswordReset(false);
        // Use window.location to ensure clean state
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(err.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteAccept = async (token) => {
    // Redirect to platform subdomain for invite acceptance
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const platformUrl = isLocalhost
      ? `http://localhost:3002/accept-invite?token=${token}` // Local dev - platform runs on separate port
      : `https://platform.empathos.com.au/accept-invite?token=${token}`; // Production subdomain
    window.location.href = platformUrl;
  };

  return (
    <div className="login-page">
      {/* Login Header Section */}
      <section className="login-intro-section">
        <div className="section-header">
          <span className="section-tag">Access</span>
          <h1>Login to Your Account</h1>
        </div>
        <div className="login-intro-content">
          <p>
            Access your Trust Intelligence<sup>&trade;</sup> dashboard and continue your journey to building stronger trust.
          </p>
        </div>
      </section>

      {/* Login Form Section */}
      <section className="login-main-section">
        <div className="login-container">
          <div className="login-form-section">
            {!showPasswordReset && !resetToken && (
              <>
            <h2>Sign In</h2>
                {error && <div className="message error">{error}</div>}
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type={isSuperuserEmail() ? "text" : "email"}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                      disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                      disabled={loading}
                />
              </div>
              {(isSuperuserEmail() || show2FAField) && (
                <div className="form-group">
                  <label htmlFor="twoFactorCode">
                    2FA Code *
                    {checking2FA && <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#B2BAC2' }}>(checking...)</span>}
                  </label>
                  <input
                    type="text"
                    id="twoFactorCode"
                    name="twoFactorCode"
                    value={twoFactorCode}
                    onChange={handle2FAChange}
                    placeholder="Enter 6-digit code"
                    required
                    disabled={loading || checking2FA}
                    maxLength={6}
                    pattern="[0-9]{6}"
                  />
                </div>
              )}
              <button 
                type="submit" 
                className="cta-primary" 
                    disabled={loading}
              >
                    {loading ? 'Signing In...' : 'Sign In'}
              </button>
              
                  <div className="login-actions">
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="form-link"
                        onClick={() => setShowPasswordReset(true)}
                        disabled={loading}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </div>
                </form>
              </>
            )}

            {showPasswordReset && !resetToken && (
              <>
                <h2>Reset Password</h2>
                {error && <div className="message error">{error}</div>}
                {resetSuccess && (
                  <div className="message success">
                    A password reset link has been sent.
                  </div>
                )}
                <form className="login-form" onSubmit={handleForgotPassword}>
                  <div className="form-group">
                    <label htmlFor="resetEmail">Email *</label>
                    <input
                      type="email"
                      id="resetEmail"
                      name="resetEmail"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      disabled={loading}
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="cta-primary" 
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="cta-secondary"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setResetEmail('');
                        setError('');
                        setResetSuccess(false);
                      }}
                      disabled={loading}
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              </>
            )}

            {resetToken && (
              <>
                <h2 id="reset-password-form">Reset Password</h2>
                {error && <div className="message error">{error}</div>}
                {resetSuccess && (
                  <div className="message success">
                    Password reset successful! Redirecting to login...
                  </div>
                )}
                <form className="login-form" onSubmit={handleResetPassword}>
                  <div className="form-group">
                    <label htmlFor="newPassword">New Password *</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      required
                      disabled={loading || resetSuccess}
                      minLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      disabled={loading || resetSuccess}
                      minLength={8}
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="cta-primary" 
                    disabled={loading || resetSuccess}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
            </form>
              </>
            )}

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-section">
              <h4>Navigation</h4>
              <nav className="footer-nav">
                <Link to="/" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Home</Link>
                <Link to="/about" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>About</Link>
                <Link to="/contact" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Contact</Link>
                <Link to="/login" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Login</Link>
              </nav>
            </div>
            
            <div className="footer-section">
              <h4>Connect</h4>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/empathostrustintelligence/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                  LinkedIn
                </a>
                <a href="mailto:team@empathos.com.au" target="_blank" rel="noopener noreferrer" aria-label="Email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Email
                </a>
              </div>
            </div>
            
            <div className="footer-section">
              <h4>Trust Intelligence<sup>&trade;</sup></h4>
              <div className="definition">
                <div className="definition-term">
                  /trʌst ɪnˈtelɪdʒəns/
                </div>
                <div className="definition-part">
                  <em>noun</em>
                </div>
                <div className="definition-meaning">
                  The measurable capacity to understand and strengthen trust between leaders and their teams.
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="copyright">
              <p><em>We acknowledge the lands of the Bidjigal People of the Eora Nation upon whose land we work.</em></p>
              <p>&copy; {new Date().getFullYear()} Empathos. All rights reserved.</p>
            </div>
            <div className="footer-logo">
              <a href="/" aria-label="Return to home">
                <img src="/empy_svg_test.svg" alt="Empathos" className="footer-logo-svg" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Login;
